import '../../modules/mode-components';

import * as angular from 'angular';

import { ExprCategory, ExprDoc } from './formula-reference-docs';
import * as FormulaReferenceDocs from './formula-reference-docs';

const defaultCategory = 'All';

class FormulaReferenceController implements angular.IController {
  static $inject = ['RxStoreService'];

  // All Data
  private documents: ExprDoc[];
  private categories: string[];

  // Select/Query/Filter
  private query: string;
  private selectedFunction: ExprDoc;
  private selectedCategory: string;
  private filtered: ExprDoc[];
  private insertFunction: (args: { signature: string }) => void;

  constructor(private readonly store) {}

  $onInit() {
    const exprDocs = FormulaReferenceDocs.make();
    // the function names can be alphanumeric in the case of the
    // percentile functions. We need to sort not only alphabetically
    // but also numerically.
    // https://stackoverflow.com/questions/2802341/javascript-natural-sort-of-alphanumerical-strings
    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: 'base',
    });
    const exprNames = Object.keys(exprDocs).sort(collator.compare);
    const exprCats = Object.keys(ExprCategory).slice();
    const documents = exprNames.map((exprName) => exprDocs[exprName]);
    const categories = exprCats.map((exprCat) => ExprCategory[exprCat]);

    this.documents = documents;
    this.filtered = documents;

    // Default the information panel to show the first document as the "selected" one by default
    this.setSelectedFunction(this.filtered[0]);

    this.categories = [defaultCategory, ...categories];
    this.setSelectedCategory(this.categories[0]);
  }

  // Getters

  isSelected(name: string) {
    return this.selectedFunction && this.selectedFunction.name === name;
  }

  // Setters

  setSelectedFunction(selected: ExprDoc) {
    this.selectedFunction = selected;
  }

  setSelectedCategory(selected: string) {
    if (selected !== this.selectedCategory) {
      this.selectedCategory = selected;
      this.onCategoryChange();
    }
  }

  // Actions
  doFilter() {
    let documents = this.documents;

    if (this.selectedCategory !== defaultCategory) {
      documents = documents.filter((doc) => doc.category === this.selectedCategory);
    }

    if (typeof this.query === 'string') {
      documents = FormulaReferenceDocs.findMatches(documents, this.query);
    }

    this.filtered = documents;
  }

  // Local Events

  onQueryChange() {
    this.doFilter();
  }

  onCategoryChange() {
    this.doFilter();
  }

  handleDropdownName() {
    if (this.selectedCategory === 'All') return 'All Functions';
    return this.selectedCategory;
  }

  setNuxtMessage() {
    return 'See all available functions and documentation here';
  }

  handleShowBadge(start: string | void, end: string) {
    const currDate = new Date();
    const startDate = start ? new Date(start) : currDate;
    const endDate = new Date(end);

    return currDate.getTime() >= startDate.getTime() && currDate.getTime() <= endDate.getTime();
  }

  emitInsert(signature: string) {
    this.insertFunction({ signature });
  }
}

const FormulaReferenceComponent: angular.IComponentOptions = {
  require: { calcEditor: '^^?' },
  bindings: {
    showReferenceNuxt: '<',
    insertFunction: '&',
  },
  template: `
    <div class="formula-reference-container">
      <div class="formula-reference-drawer">
        <div class="formula-reference-lookup">
          <div class="formula-reference-search-wrapper">

            <div class="formula-filter" uib-dropdown>
              <button
                class="formula-filter-button btn-secondary ds"
                uib-dropdown-toggle>
                <span>{{ ctrl.handleDropdownName() }}</span>
                <capra-icon icon="icon-caret-down" ></capra-icon>
              </button>

              <div uib-dropdown-menu class="formula-filter-dropdown">
                <ul class="formula-filter-list">
                  <li class="formula-filter-list-item"
                    ng-repeat="category in ctrl.categories"
                    ng-class="{'selected': category === ctrl.selectedCategory}"
                    ng-click="ctrl.setSelectedCategory(category)">
                    {{ category }}
                  </li>
                </u>
              </div>
            </div>

            <input type="text"
            ng-model="ctrl.query"
            ng-change="ctrl.onQueryChange()"
            class="form-control search-query"
            placeholder="Search..."></input>
          </div>

          <div class="document-list">
            <ul class="list-unstyled select-list">
              <li ng-repeat="document in ctrl.filtered"
                ng-focus="ctrl.setSelectedFunction(document)"
                ng-click="ctrl.setSelectedFunction(document)"
                ng-class="{'selected': ctrl.isSelected(document.name)}"
                ng-attr-title="{{document.name}}">
                <span class="document-wrapper">{{ document.name }}</span>
                <span
                  ng-if="document.flag && ctrl.handleShowBadge(document.flag.startDate, document.flag.endDate)"
                  class="document-badge {{document.flag.label}}">
                  {{ document.flag.label }}
                </span>
              </li>
            </ul>
          </div>
        </div>
        <div class="formula-reference-content">
          <div class="document-detail">
            <div class="document-detail-content" ng-if="ctrl.selectedFunction">

              <div class="document-detail-header">
                <h3 class="signature"><code>{{ ctrl.selectedFunction.sign }}</code></h3>
                <div class="reference-close"
                ng-click="ctrl.calcEditor.toggleReferenceOpen()">
                <capra-icon icon="icon-close"></capra-icon>
                </div>
              </div>
              <div class="document-detail-body">
                <p class="desc">{{ ctrl.selectedFunction.desc.trim() }}</p>

                <h4 class="examples-header">Examples</h4>

                <p class="example"
                  ng-repeat="example in ctrl.selectedFunction.examples">
                  <code>{{ example }}</code>
                </p>
              </div
            </div>
          </div>
          <div class="insert-function">
            <capra-button
              class="insert-function-button"
              text="Insert Function"
              size="medium"
              variant="secondary"
              color="neutral"
              icon="illo-mini-cal-field-add"
              (clicked)="ctrl.emitInsert(ctrl.selectedFunction.sign)"></capra-button>
          </div>
        </div>
      </div>
      <div class="toggle-reference-library"
        ng-click="ctrl.calcEditor.toggleReferenceOpen()"
        ng-class="{'is-hidden': ctrl.calcEditor.isReferenceOpen}">
        <capra-icon icon="icon-caret-right" class="toggle-reference-close"></capra-icon>
        <span>Functions</span>
        <feature-nuxt
          message="ctrl.setNuxtMessage()"
          icon-class="icon-books"
          parent-position="relative"
          position="left"
          offset-top="59px"
          offset-bottom="auto"
          ng-if="ctrl.showReferenceNuxt"
          on-close="ctrl.hideNuxt()"
        />
      </div>
    </div>
  `,
  controller: FormulaReferenceController,
  controllerAs: 'ctrl',
};

angular.module('mode.components').component('formulaReference', FormulaReferenceComponent);
